import MuiButton from "@mui/material/Button";
import { isEmpty } from "@resamare/functions";
import { useId, useRef } from "react";
import { ButtonCustom, Typography } from "../../General";
import buttonStyles from "../../General/Button/Button.module.scss";
import { Stack } from "../../Layout";
import { HtmlTextSanitizer } from "../../Utils";
import { FormControl, FormHelperText, FormInputProps, FormLabel } from "../_partials";
import styles from "./FileField.module.scss";
import { MimeExtensionsMap } from "./mimes";
import { formatExtensions, formatSize, getExtensionsFromMimes } from "./utils";

export interface FileFieldProps extends Pick<FormInputProps, "inputRef" | "name" | "placeholder"> {
    fileMaxSize: number;
    fileMimes: (keyof MimeExtensionsMap)[];
    helperText?: string;
    isError?: boolean;
    label: string;
    multiple?: boolean;
    onChange: (files: FileList | null) => void;
    value: FileList | null;
}

export function FileField({
    fileMaxSize,
    fileMimes,
    helperText = "",
    inputRef,
    isError = false,
    label,
    multiple = false,
    name,
    onChange,
    placeholder = "Aucun fichier",
    value,
}: FileFieldProps) {
    const id = useId();
    const ref = useRef<null | HTMLDivElement>(null);

    const isFilePresent = !isEmpty(value) && value !== null;

    const extensions = getExtensionsFromMimes(fileMimes);
    const fileInfos = `${formatExtensions(extensions)} (max: ${formatSize(fileMaxSize)})`;

    const handleOnClick = () => {
        const input = ref.current?.querySelector<HTMLInputElement>("input");
        if (input) {
            input.click();
        }
    };

    const placeholderText = isFilePresent ? (
        <>
            {isFilePresent && value.length === 1 ? value[0].name : null}
            {isFilePresent && value.length > 1 ? `${value.length} fichiers` : null}
        </>
    ) : (
        placeholder
    );

    return (
        <FormControl isError={isError}>
            <Stack gap={0.5}>
                <FormLabel htmlFor={id}>{label}</FormLabel>
                <FormHelperText>{fileInfos}</FormHelperText>
                <ButtonCustom
                    className={`${styles["input-container"]} ${isError ? styles["is-error"] : ""}`}
                    onClick={handleOnClick}
                >
                    <Stack alignItems="center" component="span" direction="row" gap={2}>
                        <MuiButton
                            className={`${buttonStyles["Button"]} ${buttonStyles["Button--small"]}`}
                            component="span"
                            variant="contained"
                        >
                            Choisir un fichier
                        </MuiButton>
                        <Typography
                            className={styles["placeholder"]}
                            component="span"
                            variant="text-sm"
                        >
                            {placeholderText}
                        </Typography>
                    </Stack>
                </ButtonCustom>
                <div ref={ref}>
                    <input
                        accept={fileMimes.join(", ")}
                        className={styles["input"]}
                        id={id}
                        multiple={multiple}
                        name={name}
                        onChange={(e) => {
                            onChange(e.target.files);
                        }}
                        ref={inputRef}
                        type="file"
                    />
                </div>
                {helperText ? <HtmlTextSanitizer html={helperText} variant="helper-text" /> : null}
            </Stack>
        </FormControl>
    );
}
